import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Text, Button } from '@ui-component-library';
import { TailwindWrapper } from '@utils/TailwindWrapper';
import { mapPropertiesData } from '../comps/SubscriptionCheckout/utils';
import SelectPropertyDialog from '@vb-django/components/minskog/comps/SubscriptionCheckout/components/SelectPropertyDialog';

import type {
    KYCProperty,
    KYCStep4Props,
    KYCSelectOption,
    KYCSearchConfirmData,
    KYCUserPropertyRow,
} from './KYC.types';

export const KYCStep4 = ({
    properties,
    formData,
    mergeFormData,
}: KYCStep4Props) => {
    const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
    const selectedPropertiesFromSearch = formData.selectedPropertiesFromSearch || [];
    const hasSelectedProperties = selectedPropertiesFromSearch.length > 0;

    const userProperties = useMemo((): KYCUserPropertyRow[] => mapPropertiesData(properties), [properties]);

    const selectedPropertiesForDialog = useMemo(
        () =>
            selectedPropertiesFromSearch.map((p) => ({
                ref: String(p.registerenhet || p.ref),
                designation: String(p.designation ?? p.beteckning ?? ''),
            })),
        [selectedPropertiesFromSearch],
    );

    const handleRemoveFromSelected = (ref: string) => {
        mergeFormData((prev) => {
            const refs = prev.selectedPropertyRefs || [];
            const list = prev.selectedPropertiesFromSearch || [];
            return {
                selectedPropertyRefs: refs.filter((r) => r !== ref),
                selectedPropertiesFromSearch: list.filter(
                    (p) => String(p.registerenhet || p.ref) !== ref,
                ),
            };
        });
    };

    const handlePropertySelect = (option: KYCSelectOption) => {
        const ref = String(option.value);

        mergeFormData((prev) => {
            const refs = prev.selectedPropertyRefs || [];
            const list = prev.selectedPropertiesFromSearch || [];
            if (refs.includes(ref)) {
                return {
                    selectedPropertyRefs: refs.filter((r) => r !== ref),
                    selectedPropertiesFromSearch: list.filter(
                        (p) => String(p.registerenhet || p.ref) !== ref,
                    ),
                };
            }

            const property = userProperties.find((p) => String(p.ref) === ref);
            if (!property) return {};

            const row: KYCProperty = {
                ref: property.ref,
                registerenhet: property.ref,
                designation: property.designation,
                forestArea: property.forestArea,
                type: property.type,
                lmID: property.lmID ?? undefined,
                dfr: property.dfr,
            };

            return {
                selectedPropertyRefs: [...refs, ref],
                selectedPropertiesFromSearch: [...list, row],
            };
        });
    };

    const handleSearchConfirm = (data: KYCSearchConfirmData) => {
        mergeFormData((prev) => {
            const newRefs = [...(prev.selectedPropertyRefs || [])];
            const newList = [...(prev.selectedPropertiesFromSearch || [])];

            data.properties.forEach((property) => {
                const ref = String(property.lmID);
                if (newRefs.includes(ref)) return;

                newRefs.push(ref);
                newList.push({
                    designation: property.designation,
                    ref,
                    registerenhet: ref,
                    forestArea: property.forest_land,
                    plus_price_error: property.plus_price_error ?? 0,
                    plus_price: property.plus_price ?? 0,
                    plus_price_max: property.plus_price_max ?? 0,
                    type: property.type,
                    lmID: property.lmID,
                    dfr: property.dfr,
                    hasBadData: property.has_bad_data,
                });
            });

            return {
                selectedPropertyRefs: newRefs,
                selectedPropertiesFromSearch: newList,
            };
        });
    };
    return (
        <div className="w-full">
            <Text variant="text-lg/leading-normal/medium">Lägg till fastigheter</Text>
            <Text variant="text-sm/leading-normal/normal" className="mt-2 text-[#737373]">
                Sök efter och välj fastigheter du vill aktivera i Treebula.
            </Text>
            <div className="w-full rounded-2xl bg-[#F8F4F2] p-4 mt-4">
                <div className="flex flex-row items-center justify-between gap-2">
                <Text variant="text-sm/leading-normal/medium">
                    {hasSelectedProperties ? 'Fastighet' : 'Inga fastigheter valda'}
                  </Text>
                    {!hasSelectedProperties ? (
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => setPropertyDialogOpen(true)}
                    >
                      Lägg till
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => setPropertyDialogOpen(true)}
                    >
                      Ändra
                    </Button>
                  )}
                </div>
                {hasSelectedProperties ? (
                  <div className="mt-4 flex flex-col gap-4 border-t border-solid border-[#E6E6E6] pt-4">
                    {selectedPropertiesFromSearch.map((p) => {
                      const ref = String(p.registerenhet || p.ref);
                      const ha =
                        p.forest_land ??
                        p.forestArea ??
                        p.area ??
                        p.data?.forest_land;
                      const label = String(p.designation ?? p.beteckning ?? ref);

                      return (
                        <div
                          key={ref}
                          className="flex flex-row items-center justify-between gap-2"
                        >
                          <div className="flex min-w-0 flex-col gap-1">
                            <Text variant="text-sm/leading-normal/medium" className="capitalize truncate">
                              {label.toLowerCase()}
                            </Text>
                            <Text
                              variant="text-sm/leading-normal/normal"
                              className="text-pebble-500"
                            >
                              {ha == null ? '…' : `${ha} hektar`}
                            </Text>
                          </div>
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => handleRemoveFromSelected(ref)}
                          >
                            Radera
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>

            {createPortal(
                <TailwindWrapper>
                    <SelectPropertyDialog
                        isOpen={propertyDialogOpen}
                        onClose={() => setPropertyDialogOpen(false)}
                        userProperties={userProperties}
                        selectedProperties={selectedPropertiesForDialog}
                        onSelect={handlePropertySelect}
                        onConfirm={handleSearchConfirm}
                        startedAsGuest={false}
                    />
                </TailwindWrapper>,
                document.body,
            )}
        </div>
    );
};
